/**
 * Termination, Disputes & Protection — 12 documents.
 *
 * Most CPG disputes are money: a distributor's deductions, a co-packer's failed run, a retailer's
 * chargebacks, an unpaid invoice. The outcome usually turns on whether the contract's notice and
 * cure mechanics were followed exactly — wrong method, wrong address or wrong cure period can waste
 * the right you are trying to exercise. The templates here are short notices, not litigation
 * documents. Once the other side has counsel, or once real money is at stake, you should too.
 */
export const DISPUTES_DOCS = [
  {
    id: "termination-agreement",
    name: "Termination Agreement",
    category: "disputes",
    subcategory: "Ending a relationship",
    aliases: [
      "Contract Termination Agreement",
      "Agreement to Terminate",
      "Wind-Down Agreement",
    ],
    stages: ["distribution", "growth"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Brand + Counterparty to the Agreement Being Ended",
    whenYouNeedIt:
      "When you are ending a signed agreement — a distributor, a co-packer, a broker, an agency — and you want the ending documented rather than left to a chain of emails and one final invoice.",
    whatItDoes:
      "Ends the agreement on agreed terms and settles what sits behind it: final orders and invoices, inventory, tooling, artwork, data, deposits, which obligations survive, and whether either side gives up claims.",
    whyItMatters:
      "CPG relationships rarely end cleanly. They end with inventory in someone else's warehouse, an open deduction balance, a customer list you want back, and a co-packer holding your tooling. A termination agreement resolves those while both sides still want something from each other. The alternative is finding out six months later that a former distributor still has product in the market, or that a co-packer is treating your mould as security for a disputed invoice. Read the notice clause in the original agreement before you send anything — how you terminate can matter more than that you terminated.",
    majorTerms: [
      "Effective termination date",
      "Final orders, final production run and last shipment",
      "Treatment of inventory in the counterparty's hands",
      "Outstanding invoices, deductions and credits",
      "Return of tooling, moulds, artwork, plates and specifications",
      "Return or destruction of confidential information and data",
      "Customer lists, accounts and transition of relationships",
      "Which obligations survive: confidentiality, non-solicitation, indemnity",
      "Any wind-down services and who pays for them",
      "Whether either party releases claims",
      "Communications to shared customers",
      "Any payment passing on termination",
    ],
    clausesToWatch: [
      {
        clause: "Whether claims are released",
        why: "A termination agreement that quietly includes a release ends claims you may not have identified yet, so you need to know what you are giving up before you sign it.",
      },
      {
        clause: "Inventory in the counterparty's hands",
        why: "Unsold product left with a distributor or 3PL keeps reaching the market under your name, at prices you no longer control.",
      },
      {
        clause: "Return of tooling, artwork and specifications",
        why: "Without a stated obligation and a deadline, physical assets stay where they are and become leverage in the next disagreement.",
      },
      {
        clause: "Survival of confidentiality and non-solicitation",
        why: "The obligations you most want after a relationship ends are exactly the ones that lapse when the termination agreement is silent.",
      },
      {
        clause: "Deduction and credit reconciliation",
        why: "An unreconciled balance at termination tends to settle in favour of whoever is holding the money, which is not usually you.",
      },
      {
        clause: "Customer and account transition",
        why: "Accounts opened under the relationship can stay with the counterparty unless the agreement says who they belong to.",
      },
      {
        clause: "Communications to shared customers",
        why: "An uncoordinated announcement lets the other side tell your accounts their version of events first.",
      },
    ],
    cpgIssues: [
      "A terminated distributor often holds weeks of your inventory, and that product is ageing against a code date for the whole length of the negotiation.",
      "Tooling, moulds, change parts and printing plates held by a co-packer or packaging supplier cannot be re-made quickly, so recovering them needs a date and a delivery method written in.",
      "Specifications, formulas and quality records held by a manufacturer should be returned or destroyed on a stated timetable, particularly where the facility also makes competing products.",
      "Open deductions, spoils and promotional bill-backs with a distributor or retailer are almost always unresolved at termination, and reconciling them afterwards is far harder.",
      "Retail accounts opened under a broker or distributor relationship need an orderly handover, or the item goes out of stock and gets delisted during the transition.",
      "Remaining packaging and labels carrying the counterparty's details — a distributor's contact information, an importer's address — become unusable, and somebody has to absorb that.",
      "Lot and traceability records must stay accessible after termination, because a withdrawal may still be needed on product made or distributed during the relationship.",
    ],
    founderMistakes: [
      "Terminating by email without reading the notice clause, and finding the termination ineffective while the term rolled over.",
      "Signing a termination agreement containing a broad release, then discovering a quality problem in product made months earlier.",
      "Leaving inventory with a terminated distributor and watching it appear discounted in the accounts they were about to relaunch.",
      "Never asking for the tooling back, and being quoted a large sum for it at the moment they needed it.",
      "Letting the counterparty announce the change to shared accounts first.",
      "Walking away from an unreconciled deduction balance because the relationship was over anyway.",
    ],
    negotiationPoints: [
      "Read the notice clause in the original agreement and follow it exactly before negotiating anything.",
      "Set a date and a method for the return of tooling, artwork, specifications and data.",
      "Agree what happens to remaining inventory: buy-back, a sell-off window with price limits, or destruction.",
      "Reconcile deductions, credits and open invoices as part of the termination rather than afterwards.",
      "State expressly which obligations survive, particularly confidentiality and non-solicitation.",
      "Agree a joint communication to shared customers and the timing of it.",
      "Be clear about whether you are releasing claims, and price that if you are.",
    ],
    redFlags: [
      "A broad release inserted into what was presented as a simple termination.",
      "No date or method for the return of tooling, artwork or data.",
      "No treatment of inventory the counterparty is holding.",
      "Silence on which obligations survive termination.",
      "An unreconciled deduction or credit balance left open.",
      "Pressure to sign quickly in order to release a final payment.",
    ],
    beforeYouSign: [
      "The original agreement's notice and termination clauses, read carefully first.",
      "A full list of what the counterparty holds: inventory, tooling, artwork, data, records.",
      "A reconciled statement of invoices, credits and deductions.",
      "Counsel's view on any release you are being asked to give.",
    ],
    related: [
      "mutual-termination-agreement",
      "notice-of-termination",
      "notice-to-cure",
      "settlement-agreement",
      "mutual-release",
    ],
    attorneyReview: "yes",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "mutual-termination-agreement",
    name: "Mutual Termination Agreement",
    category: "disputes",
    subcategory: "Ending a relationship",
    aliases: [
      "Mutual Rescission Agreement",
      "Termination by Consent",
      "Mutual Wind-Down Agreement",
    ],
    stages: ["distribution", "growth"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Brand + Counterparty, by agreement",
    whenYouNeedIt:
      "When both sides want out and neither is claiming the other did anything wrong: a distributor relationship that never worked, a co-packer whose line no longer suits your volume, an agency retainer both sides have outgrown.",
    whatItDoes:
      "Ends the agreement by consent on a stated date, settling what happens to inventory, money, materials and continuing obligations, and usually with each side releasing the other from claims arising out of the relationship.",
    whyItMatters:
      "The mutual version is the cheapest exit available and it is only available while both sides still want it. It avoids arguing about who breached, sidesteps notice and cure mechanics, and lets you plan an orderly handover instead of a scramble. The trap is the release: agreeing that nothing is owed either way before you have checked the deduction balance, the quality history and the inventory position, which is exactly where the money you did not know about tends to sit.",
    majorTerms: [
      "Effective date, and that termination is by consent",
      "Confirmation that neither party alleges breach",
      "Final orders, production and shipment",
      "Inventory disposition and any buy-back",
      "Reconciliation of invoices, credits and deductions",
      "Return of tooling, artwork, specifications, files and system access",
      "Survival of confidentiality, non-solicitation and indemnity",
      "Mutual release and any claims carved out of it",
      "Transition of accounts and customer communications",
      "Any payment passing between the parties",
      "Non-disparagement",
      "Confidentiality of the terms",
    ],
    clausesToWatch: [
      {
        clause: "Scope of the mutual release",
        why: "A release covering everything arising from the relationship covers claims nobody has discovered yet, including product problems that surface later.",
      },
      {
        clause: "Carve-outs from the release",
        why: "Product liability, recall exposure, indemnity and third-party claims are usually exactly what you want kept outside it.",
      },
      {
        clause: "Inventory disposition",
        why: "Unsold product left with the other side keeps reaching your customers on terms you have stopped controlling.",
      },
      {
        clause: "Reconciliation of money",
        why: "A mutual termination signed before the deduction and credit balance is reconciled usually settles that balance at zero by default.",
      },
      {
        clause: "Survival of confidentiality",
        why: "The practical value of a confidentiality obligation is entirely in the period after the relationship ends.",
      },
      {
        clause: "Non-disparagement and communications",
        why: "Both sides will be speaking to the same accounts next week, and in a small category that matters more than the wording suggests.",
      },
    ],
    cpgIssues: [
      "Quality issues in product already made and sold can surface months after a mutual termination, so consider carving product liability and recall obligations out of any release.",
      "Inventory ageing in a distributor's or 3PL's warehouse loses value every week the negotiation continues, which is a reason to move fast rather than to concede.",
      "Co-packers hold tooling, change parts and specifications, and a mutual termination is the easiest moment to get them back with a date attached.",
      "Open promotional bill-backs and spoils allowances rarely net to zero, so reconcile before agreeing that nothing is owed either way.",
      "Shared retail accounts need a coordinated handover, or the item goes out of stock at exactly the moment the relationship changes.",
      "Remaining printed packaging showing the other party's details is a write-off somebody has to absorb, and it should be named rather than discovered.",
      "Traceability and quality records for product made during the relationship should stay available to both sides for as long as that product could still be in the market.",
    ],
    founderMistakes: [
      "Signing a broad mutual release to end a relationship quickly, then finding a quality problem in product made under it.",
      "Agreeing that all accounts were settled without reconciling the deduction balance first.",
      "Leaving inventory with the other party and having it discounted straight into their own accounts.",
      "Not recovering tooling and specifications while goodwill still existed.",
      "Ending an agency relationship without securing files, assets and platform access.",
      "Announcing the change to customers with no coordination, and losing accounts in the confusion.",
    ],
    negotiationPoints: [
      "Reconcile invoices, credits and deductions before agreeing any release.",
      "Carve product liability, recall and third-party claims out of the mutual release.",
      "Set a date and method for return of tooling, artwork, files and platform access.",
      "Agree inventory disposition explicitly, including any price floor on a sell-off.",
      "State which obligations survive, especially confidentiality and non-solicitation.",
      "Agree a joint message to shared accounts and when it goes out.",
    ],
    redFlags: [
      "A mutual release signed before the money has been reconciled.",
      "No carve-out for product liability or recall exposure.",
      "No obligation to return tooling, artwork, records or system access.",
      "Inventory left with the other party with no restrictions on how it is sold.",
      "No survival clause for confidentiality.",
      "Pressure to sign the release in order to unlock a final payment.",
    ],
    beforeYouSign: [
      "A reconciled statement of every invoice, credit, deduction and allowance.",
      "An inventory of what the other party holds, physically and digitally.",
      "Counsel's view on the release scope and what should be carved out of it.",
    ],
    related: [
      "termination-agreement",
      "mutual-release",
      "settlement-agreement",
      "notice-of-termination",
      "general-release",
    ],
    attorneyReview: "yes",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "settlement-agreement",
    name: "Settlement Agreement",
    category: "disputes",
    subcategory: "Settlement and releases",
    aliases: [
      "Settlement and Release Agreement",
      "Compromise Agreement",
      "Dispute Resolution Agreement",
    ],
    stages: ["distribution", "growth"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Brand + Opposing Party",
    whenYouNeedIt:
      "When a dispute is being resolved without going further: an unpaid balance being compromised, a failed production run being credited, a distributor exit being bought out, a claim being dropped.",
    whatItDoes:
      "Records the deal that ends the dispute — what is paid, by when, what each side gives up, what each side is released from, and what happens if the payment does not arrive.",
    whyItMatters:
      "A settlement is usually the cheapest outcome available and it is also the document where founders give away the most without noticing. The release is the consideration: it is what the other side is buying, which makes its scope the whole negotiation rather than a formality at the end. A broadly drafted release ends claims nobody has discovered, including claims about product already in the market. And a settlement with no payment default mechanism leaves you holding a piece of paper and the same problem you started with.",
    majorTerms: [
      "The payment or other consideration and the exact schedule",
      "Security, acceleration or consequences if a payment is missed",
      "Scope of the release each party gives",
      "Carve-outs from the release",
      "Whether unknown claims are covered",
      "Who else is released: affiliates, officers, insurers, successors",
      "Confidentiality of the settlement terms",
      "Non-disparagement",
      "No admission of liability",
      "Return or destruction of property, inventory and information",
      "Whether business between the parties continues, and on what terms",
      "Withdrawal of any pending proceedings",
      "Governing law and enforcement",
    ],
    clausesToWatch: [
      {
        clause: "Scope of the release",
        why: "This is what you are actually selling in exchange for the money, and a broad one ends claims nobody has found yet.",
      },
      {
        clause: "Whether unknown claims are covered",
        why: "Language extending a release to claims you do not yet know about is common and consequential, and deserves counsel's attention specifically.",
      },
      {
        clause: "Payment default consequences",
        why: "A settlement paid in instalments with no security and no acceleration is a promise rather than a resolution.",
      },
      {
        clause: "Who is released",
        why: "Releases often extend to affiliates, officers and related entities, which can be far broader than the party you were actually arguing with.",
      },
      {
        clause: "Carve-outs",
        why: "Product liability, indemnity, recall obligations and future business are usually the things you want to keep outside the release.",
      },
      {
        clause: "Confidentiality and non-disparagement",
        why: "In a small category what each side may say afterwards has real commercial value, in both directions.",
      },
      {
        clause: "Whether business continues",
        why: "Settling with a party you still trade with needs the go-forward terms restated, or the same dispute returns next quarter.",
      },
    ],
    cpgIssues: [
      "Settling a co-packer dispute over a failed run usually means releasing claims about product that may already be on shelf, so the release should not swallow future product liability or recall cost.",
      "Distributor settlements involve inventory as well as money: who buys it back, at what price, and whether it may be sold into the same accounts afterwards.",
      "Retailer and distributor deduction disputes settle as a net figure, and it is worth knowing what sits inside that figure before releasing every open deduction.",
      "A release given to an ingredient or packaging supplier can extinguish the claim you would need if their material later turns out to be the cause of a defect in finished goods.",
      "Where a settlement involves product being destroyed, require evidence of destruction rather than an assurance, particularly for branded goods.",
      "Settling with a former distributor or licensee should address what they may do with remaining branded materials, packaging and market claims.",
      "Quality and traceability records held by the other party should be preserved as part of the settlement, because a withdrawal may still need them.",
    ],
    founderMistakes: [
      "Signing a broad release to recover a payment, and giving up a product liability claim they did not know they had.",
      "Accepting an instalment settlement with no security, and receiving only the first payment.",
      "Settling a deduction dispute as one net number without knowing what was inside it.",
      "Releasing an entity's affiliates without realising the group included a supplier they had a separate problem with.",
      "Agreeing to destruction of branded inventory and never receiving any evidence it happened.",
      "Settling without addressing whether the relationship continued, and repeating the same dispute the following quarter.",
    ],
    negotiationPoints: [
      "Negotiate the scope of the release as hard as the number, because it is what you are selling.",
      "Carve out product liability, recall and indemnity claims wherever the dispute involves product.",
      "Secure instalment payments and include acceleration and a consequence for default.",
      "Limit who is released to the parties actually involved in the dispute.",
      "Require evidence of destruction where inventory is being destroyed.",
      "Restate the go-forward terms if you are continuing to trade together.",
      "Agree confidentiality and non-disparagement in a category where everybody talks.",
    ],
    redFlags: [
      "A release covering unknown claims agreed without counsel's advice.",
      "Instalment payments with no security and no acceleration.",
      "A release extending to affiliates and related entities you have not identified.",
      "No carve-out for product liability or recall in a product dispute.",
      "Destruction of goods with no evidence requirement.",
      "A same-day deadline to sign in order to unlock a payment.",
    ],
    beforeYouSign: [
      "Counsel's review of the release scope, including who and what it covers.",
      "A schedule of every open claim, deduction and invoice between the parties.",
      "Payment security, or an enforceable consequence if instalments are missed.",
      "Confirmation of what happens to any inventory, tooling or records the other side holds.",
    ],
    related: [
      "mutual-release",
      "general-release",
      "termination-agreement",
      "demand-letter",
      "collection-demand",
    ],
    attorneyReview: "strongly-recommended",
    attorneyNote:
      "The release is what you are actually giving in exchange for the money, and its scope — particularly around unknown claims and future product liability — should be reviewed by counsel before signature.",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "mutual-release",
    name: "Mutual Release",
    category: "disputes",
    subcategory: "Settlement and releases",
    aliases: [
      "Mutual Release of Claims",
      "Reciprocal Release",
      "Release and Waiver (Mutual)",
    ],
    stages: ["distribution", "growth"],
    importance: "situational",
    entityTypes: ["any"],
    parties: "Brand + Counterparty, each releasing the other",
    whenYouNeedIt:
      "When two parties want to close the book on each other: at the end of a relationship, alongside a settlement, or after a dispute has resolved commercially and both sides want it to stay resolved.",
    whatItDoes:
      "Each party gives up claims against the other arising from a defined relationship or set of events, on a stated scope, with whatever carve-outs the parties agree.",
    whyItMatters:
      "A mutual release sounds even-handed and frequently is not, because the two sides rarely carry symmetric risk. In CPG the brand usually carries the product exposure: if a co-packer's run causes a problem, the claims arrive at the brand, and a release signed today can cover the claim you would want against the manufacturer tomorrow. The other side's exposure is often limited to money you owe them. Look hard at who has more to release before agreeing to release the same amount.",
    majorTerms: [
      "The relationship or events being released",
      "Effective date and the cut-off for claims covered",
      "Whether unknown claims are included",
      "Carve-outs from the release",
      "Who is covered on each side: entities, officers, insurers, successors",
      "Consideration given for the release",
      "Confidentiality of the release and its terms",
      "Non-disparagement",
      "No admission of liability",
      "Obligations that continue despite the release",
      "Return of property and information",
      "Governing law",
    ],
    clausesToWatch: [
      {
        clause: "Whether unknown claims are included",
        why: "Extending a release to claims you have not discovered is common wording and is the feature most likely to matter later.",
      },
      {
        clause: "The cut-off date",
        why: "A release covering everything up to signature can include a product problem already in the market but not yet reported to anyone.",
      },
      {
        clause: "Carve-outs",
        why: "Product liability, recall, indemnity and third-party claims are usually exactly what you want to preserve.",
      },
      {
        clause: "Who is covered",
        why: "Releases extending to affiliates and related entities can cover a party you have a separate live issue with.",
      },
      {
        clause: "Symmetry of exposure",
        why: "Mutual wording does not mean equal risk, and in a manufacturing or supply relationship the brand usually has far more to lose by releasing.",
      },
      {
        clause: "Survival of confidentiality and IP terms",
        why: "A release should not accidentally end obligations you were relying on continuing.",
      },
    ],
    cpgIssues: [
      "A release given to a co-packer covers product already produced and possibly already on shelf, which is precisely where the brand's real exposure sits.",
      "Ingredient and packaging supplier releases can extinguish the claim you would need if their material is later found to be the cause of a defect in finished goods.",
      "Distributor releases often coincide with inventory still in the market, so the release date and the inventory position have to be considered together.",
      "Where a quality investigation is open, or test results are outstanding, a release signed before the results arrive is a release given blind.",
      "Recall cooperation and record-retention obligations should survive any release, because a withdrawal can be needed long afterwards.",
      "In a small category the same parties meet again, so confidentiality and non-disparagement terms carry more practical value than they appear to.",
    ],
    founderMistakes: [
      "Signing a mutual release with a manufacturer while a quality investigation was still open.",
      "Releasing everything up to the signature date with product from a suspect run still in distribution.",
      "Agreeing a release covering the counterparty's affiliates, one of which was a separate supplier with its own open issue.",
      "Treating mutual as equal, when the brand carried all the product exposure and the other side carried only an invoice.",
      "Releasing without preserving recall cooperation and record-retention obligations.",
      "Giving a release for a payment that then arrived late and short.",
    ],
    negotiationPoints: [
      "Carve out product liability, recall, indemnity and third-party claims.",
      "Set the cut-off date deliberately rather than defaulting to the signature date.",
      "Limit the released parties to those actually involved.",
      "Preserve confidentiality, IP and recall cooperation obligations expressly.",
      "Avoid giving the release before the consideration has actually been received.",
      "Have counsel look at whether unknown claims are being released and what that means for you.",
    ],
    redFlags: [
      "A release given while a quality or safety investigation is open.",
      "Unknown claims released with no advice taken.",
      "No carve-out for product liability or recall in a product relationship.",
      "A release extending to affiliates you have not identified.",
      "A release given before payment is received.",
      "Survival of confidentiality and IP obligations left unaddressed.",
    ],
    beforeYouSign: [
      "A list of every open matter between the parties, including quality investigations.",
      "Counsel's view on the release scope, the cut-off date and the carve-outs.",
      "Confirmation that any consideration has been received or is properly secured.",
    ],
    related: [
      "general-release",
      "settlement-agreement",
      "mutual-termination-agreement",
      "termination-agreement",
      "confidentiality-breach-notice",
    ],
    attorneyReview: "strongly-recommended",
    attorneyNote:
      "Mutual wording does not mean equal exposure, and in product relationships the brand usually gives up more than it receives — counsel should review the scope, the cut-off date and the carve-outs.",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "general-release",
    name: "General Release",
    category: "disputes",
    subcategory: "Settlement and releases",
    aliases: [
      "Release of Claims",
      "Waiver and Release",
      "One-Way Release",
    ],
    stages: ["distribution", "growth"],
    importance: "situational",
    entityTypes: ["any"],
    parties: "Releasing Party + Released Party",
    whenYouNeedIt:
      "When one side gives up claims against the other, typically as the condition of receiving a payment, ending an engagement, or closing out an incident.",
    whatItDoes:
      "One party releases the other from claims of a defined scope, usually in exchange for money or another benefit, sometimes extending to claims the releasing party does not yet know it has.",
    whyItMatters:
      "This is the shortest document in the category and the one with the widest consequences. A release signed to get paid can waive claims a founder did not know existed: a quality issue that has not surfaced, an indemnity that has not been called, a chargeback series that has not landed yet. Once given, it is not usually something you can revisit because new facts appeared. Whenever a release turns up as a routine condition of a payment, that is the moment to slow down rather than the moment to sign quickly.",
    majorTerms: [
      "Identity of the releasing and released parties",
      "The claims and the relationship covered",
      "Cut-off date for claims",
      "Whether unknown claims are included",
      "The consideration given for the release",
      "Carve-outs",
      "Confidentiality and non-disparagement",
      "No admission of liability",
      "Whether the release takes effect only on cleared funds",
      "Governing law",
    ],
    clausesToWatch: [
      {
        clause: "Whether unknown claims are covered",
        why: "This single feature turns a release from a tidy-up into a decision with consequences you cannot yet see.",
      },
      {
        clause: "Conditionality on payment",
        why: "A release effective on signature rather than on cleared funds gives away the claim before you actually have the money.",
      },
      {
        clause: "Scope of the relationship covered",
        why: "A broadly drafted release can reach transactions and relationships well beyond the one being settled.",
      },
      {
        clause: "Released parties",
        why: "Officers, affiliates, insurers and successors are often included, extending the release far past the counterparty you were dealing with.",
      },
      {
        clause: "Carve-outs",
        why: "Without them, obligations you rely on — indemnity, recall cooperation, confidentiality — can disappear along with everything else.",
      },
      {
        clause: "Who is asking, and why now",
        why: "A release presented as routine paperwork at the moment money is due is a negotiation, not an administrative step.",
      },
    ],
    cpgIssues: [
      "Retailers and distributors sometimes require a release to settle a deduction balance, which can extinguish every open claim across a trading relationship in exchange for one number.",
      "A release given to a co-packer or ingredient supplier can cover a latent defect that has not yet appeared in finished goods.",
      "Releases sought at the end of a recall or withdrawal deserve particular care, because the full cost of an event is rarely known while it is still running.",
      "A release from a departing broker, agency or contractor should be checked against the IP assignment and confidentiality obligations you still need from them.",
      "Releases signed to close a consumer complaint belong with counsel, because the facts and the exposure are usually incomplete at that stage.",
      "Where product from the covered period is still in the market, the cut-off date matters more than the amount being paid.",
    ],
    founderMistakes: [
      "Signing a release to unlock a held payment, and giving up an unrelated claim in the same paragraph.",
      "Releasing before the payment cleared, then having to chase it with no leverage left.",
      "Settling a retailer deduction balance with a general release and losing every open claim across the year.",
      "Releasing a supplier while a stability or contamination question was still unresolved.",
      "Not noticing the release extended to affiliates and successors.",
      "Treating a one-page release as administrative and never showing it to counsel.",
    ],
    negotiationPoints: [
      "Make the release effective only when funds have actually cleared.",
      "Narrow the scope to the specific matter being settled.",
      "Exclude unknown claims, or accept them only with advice and with carve-outs.",
      "Limit the released parties to the counterparty itself.",
      "Preserve indemnity, confidentiality, IP and recall obligations expressly.",
      "Ask why a release is being requested now, and what the other side believes it is buying.",
    ],
    redFlags: [
      "A release presented as a condition of a payment already owed.",
      "Unknown claims released with no advice.",
      "A release effective on signature rather than on cleared funds.",
      "Scope extending beyond the matter being settled.",
      "Affiliates, successors and insurers included without discussion.",
      "A same-day signing deadline.",
    ],
    beforeYouSign: [
      "Counsel's review, however short the document looks.",
      "A written list of every open or possible claim you have against the other side.",
      "Confirmation of when the money actually arrives, and whether the release can be tied to it.",
    ],
    related: [
      "mutual-release",
      "settlement-agreement",
      "demand-letter",
      "collection-demand",
      "separation-agreement",
    ],
    attorneyReview: "strongly-recommended",
    attorneyNote:
      "A one-page release can waive claims that have not yet surfaced, including product exposure, and it is almost always presented at the moment when signing quickly feels most attractive.",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "demand-letter",
    name: "Demand Letter",
    category: "disputes",
    subcategory: "Notices and demands",
    aliases: [
      "Letter of Demand",
      "Formal Demand",
      "Pre-Action Letter",
    ],
    stages: ["distribution", "growth"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Brand + Counterparty",
    whenYouNeedIt:
      "When something is owed or something has to stop, informal requests have not worked, and you want a dated record that a formal demand was made.",
    whatItDoes:
      "States what happened, what you say the other side owes or must do, by when, and what you intend to do if they do not — in a form that becomes part of the record of the dispute.",
    whyItMatters:
      "A demand letter is a business tool with legal consequences. It is often the fastest way to get paid, because it moves a problem from somebody's inbox to somebody's desk. It is also a document the other side keeps, quotes back and shows to their lawyer, so overstating facts, threatening what you will not do, or citing the wrong contract all cost you credibility at the exact moment you need it. Send nothing before checking the agreement's notice provisions — method, address and timing — because a demand that ignores them can waste the very right you are trying to exercise.",
    majorTerms: [
      "The agreement or transaction concerned",
      "A dated, specific factual account",
      "Exactly what is demanded: an amount, an action, or both",
      "The basis for the demand under the agreement",
      "A clear deadline",
      "What you intend to do if the deadline passes",
      "Where and how a response should be sent",
      "Documents attached: invoices, purchase orders, photographs, records",
      "Reservation of rights",
      "The correct method and address for notices under the agreement",
      "Signature by somebody authorised",
    ],
    clausesToWatch: [
      {
        clause: "The notice provision of the underlying agreement",
        why: "Sending to the wrong address or by the wrong method can mean the notice does not count for anything, whatever it says inside.",
      },
      {
        clause: "The deadline you set",
        why: "A deadline you do not act on teaches the other side that your deadlines are decorative, which makes the next one worthless.",
      },
      {
        clause: "What you threaten",
        why: "Naming a consequence you will not follow through on removes the only leverage the letter had.",
      },
      {
        clause: "Accuracy of the facts and figures",
        why: "One wrong date or number lets the other side answer the error instead of the substance.",
      },
      {
        clause: "Reservation of rights",
        why: "Without it, demanding one thing can look like acceptance of everything else that has happened.",
      },
      {
        clause: "Whether this is the right first step",
        why: "Some agreements require a formal notice to cure first, and a demand letter is not a substitute for one.",
      },
    ],
    cpgIssues: [
      "Most CPG demands concern money that was taken rather than money never paid — deductions, chargebacks, spoils and bill-backs — so attach the deduction detail and whatever backup you have.",
      "Co-packer disputes turn on the specification and the batch records, so a demand about a failed run should reference the signed spec and the lot numbers rather than describing the product as wrong.",
      "Distributor disputes usually involve inventory as well as money, so say what should happen to the product as well as what you want paid.",
      "Quality claims need lot codes, dates, retained samples and any testing, and a demand without them invites a long argument about whether the product was ever yours.",
      "Retail deduction disputes have short documentation windows, so a demand arriving after the window has closed rarely recovers anything.",
      "Where product is still shipping, say plainly whether shipments continue, stop, or continue only on changed terms.",
    ],
    founderMistakes: [
      "Sending an angry email instead of a demand, and creating a document they later had to explain.",
      "Ignoring the contract's notice provision and sending to a sales contact with no authority to act on it.",
      "Setting a deadline and then doing nothing for three months.",
      "Overstating the amount owed and spending the next month arguing about the calculation instead of the debt.",
      "Threatening litigation they had no intention of funding.",
      "Demanding payment while continuing to ship, removing any urgency for the other side.",
    ],
    negotiationPoints: [
      "Read the notice clause first and follow it exactly on method, address and any required copies.",
      "State a specific number and show how it is calculated, with the documents attached.",
      "Set a deadline you will actually act on.",
      "Say what happens next, and name only consequences you are prepared to follow through.",
      "Decide whether to keep shipping before the letter goes out, and say so in it.",
      "Have counsel review it where the amount is material, where the other side has lawyers, or where it might be quoted back at you.",
    ],
    redFlags: [
      "Sending anything before checking the notice clause.",
      "Facts or figures you have not verified against records.",
      "A threat you will not carry out.",
      "No deadline, or one nobody intends to enforce.",
      "A demand sent while you continue performing as though nothing is wrong.",
      "Sending it yourself when the other side already has counsel involved.",
    ],
    beforeYouSign: [
      "The notice provision of the underlying agreement, read and followed.",
      "The documents that prove the claim: invoices, purchase orders, spec sheets, deduction detail, lot records.",
      "A decision about what you will actually do if the deadline passes.",
    ],
    related: [
      "collection-demand",
      "breach-notice",
      "notice-to-cure",
      "settlement-agreement",
      "general-release",
    ],
    attorneyReview: "yes",
    templateId: "demand-letter",
    templateVersion: "1.0",
    lastReviewed: "2026-08-25",
  },

  {
    id: "breach-notice",
    name: "Breach Notice",
    category: "disputes",
    subcategory: "Notices and demands",
    aliases: [
      "Notice of Breach",
      "Notice of Default",
      "Default Notice",
    ],
    stages: ["distribution", "growth"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Brand + Counterparty in Breach",
    whenYouNeedIt:
      "When a contracting party has failed to do something the agreement requires and you want that failure formally on the record — usually as the first step before cure, termination or a claim.",
    whatItDoes:
      "Identifies the agreement, states the specific obligation that has not been met and the facts showing it, puts the other side formally on notice, reserves your rights and points to what comes next.",
    whyItMatters:
      "The value of a breach notice is almost entirely procedural. Most agreements condition your best remedies — termination, damages, stopping performance — on having given notice in the way the contract describes. Get the method, the address, the description or the timing wrong and you can find months later that the clock never started. Founders skip this step because the relationship still feels workable, and then discover they cannot exercise the right they had been counting on.",
    majorTerms: [
      "The agreement, its date and the parties",
      "The specific clause said to have been breached",
      "The facts, dated and evidenced",
      "Whether the breach is capable of being cured",
      "Any cure period the agreement provides",
      "What you require, and by when",
      "Reservation of all rights and remedies",
      "Whether performance continues in the meantime",
      "Method and address for notices, taken from the agreement",
      "Any required copies to counsel or a second address",
      "Attachments evidencing the breach",
      "Signature by somebody authorised",
    ],
    clausesToWatch: [
      {
        clause: "The notice provision",
        why: "Method, address, copies and deemed-receipt timing are all specified in most agreements, and any one of them can invalidate the notice.",
      },
      {
        clause: "The clause you cite",
        why: "Citing the wrong obligation gives the other side an easy answer and can waste the notice entirely.",
      },
      {
        clause: "Whether a cure period applies",
        why: "Some breaches are curable and some are not, and treating one as the other starts the wrong process.",
      },
      {
        clause: "Reservation of rights",
        why: "Without it, accepting continued performance after a breach can look like you have let it go.",
      },
      {
        clause: "Whether you keep performing",
        why: "Continuing to ship on open credit after notifying a payment breach undermines the notice you just sent.",
      },
      {
        clause: "Timing",
        why: "Some rights are lost if not exercised within a stated period after you become aware of the failure.",
      },
    ],
    cpgIssues: [
      "Co-packer breaches are usually specification failures, so the notice should reference the signed specification, the lot numbers and any test results rather than describing the product as simply wrong.",
      "Distributor breaches are usually missed performance minimums, territory violations or unpaid invoices, and each is evidenced completely differently.",
      "Quality-related breaches carry a parallel operational track — holds, retained samples, testing, possible withdrawal — which runs on its own timetable regardless of the notice.",
      "Retail and distributor deduction disputes have documentation windows that run independently of any contractual notice period, and missing them forfeits the money whatever the notice says.",
      "Supplier breaches on ingredient origin, allergen status or certification affect what your own label claims, so the notice should preserve claims relating to finished goods already produced.",
      "Where a breach may lead to termination, inventory, tooling and registrations all still need dealing with, so the notice is the start of a plan rather than the end of one.",
    ],
    founderMistakes: [
      "Raising a breach by email to a day-to-day contact and never sending formal notice at all.",
      "Sending notice to the wrong address and losing months when the other side said it was never validly given.",
      "Citing the wrong clause and having the notice answered on a technicality.",
      "Continuing to ship on open credit while notifying a payment breach.",
      "Failing to send within a time limit the agreement imposed.",
      "Sending a breach notice as a negotiating tactic without deciding what to do if it was ignored.",
    ],
    negotiationPoints: [
      "Read the notice clause and follow it exactly, including any copy-to requirement.",
      "Cite the specific obligation and attach the evidence.",
      "Reserve all rights expressly, and state that continued performance is not a waiver.",
      "Decide before sending whether you will keep performing, and then be consistent.",
      "Ask counsel to review before sending if termination is a realistic next step.",
      "Track any cure period from the date the notice is deemed received under the agreement, not the date you sent it.",
    ],
    redFlags: [
      "Sending anything before reading the notice provision.",
      "A breach described in general terms with no clause cited and no evidence attached.",
      "No reservation of rights.",
      "Continuing to perform in a way that contradicts the notice.",
      "Sending it yourself when the counterparty already has lawyers involved.",
      "No decision made about what happens when the cure period ends.",
    ],
    beforeYouSign: [
      "The agreement's notice and cure provisions, read in full.",
      "The evidence: specifications, lot records, test results, invoices, correspondence.",
      "A decision about what you will do at the end of the cure period.",
    ],
    related: [
      "notice-to-cure",
      "notice-of-termination",
      "demand-letter",
      "termination-agreement",
      "quality-agreement",
    ],
    attorneyReview: "yes",
    templateId: "breach-notice",
    templateVersion: "1.0",
    lastReviewed: "2026-08-25",
  },

  {
    id: "notice-to-cure",
    name: "Notice to Cure",
    category: "disputes",
    subcategory: "Notices and demands",
    aliases: [
      "Cure Notice",
      "Notice to Remedy",
      "Opportunity to Cure Letter",
    ],
    stages: ["distribution", "growth"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Brand + Counterparty",
    whenYouNeedIt:
      "When an agreement gives the other side a period to fix a failure before you can terminate or claim, and you intend to start that clock running.",
    whatItDoes:
      "Formally identifies the failure, states what would count as a cure, and starts the cure period the agreement provides — after which the rights conditioned on it become available to you.",
    whyItMatters:
      "This is the most procedurally fragile document in the category and the one founders most often get wrong. The cure period, the delivery method, the address and what counts as a cure are all set by the contract, and a notice that misses any of them may never start the clock at all. The practical failure looks like this: a founder emails in March, terminates in May, and finds the distributor arguing the cure period never began because notice had to go by courier to a different address. Read the notice clause before you send anything.",
    majorTerms: [
      "The agreement and the clause relied on",
      "The specific failure, dated and evidenced",
      "What would constitute a cure, stated concretely",
      "The cure period and the date it ends",
      "The consequence if the failure is not cured",
      "Method and address for delivery, taken from the agreement",
      "Any copy-to requirement",
      "Whether performance continues during the cure period",
      "Reservation of rights",
      "Attachments evidencing the failure",
      "Signature by somebody authorised",
    ],
    clausesToWatch: [
      {
        clause: "The exact cure period and how it is counted",
        why: "Business days, calendar days and deemed-receipt rules all change the end date, and acting a day early can invalidate everything that follows.",
      },
      {
        clause: "Delivery method and address",
        why: "This is the single most common reason a cure notice fails to do the job it was sent to do.",
      },
      {
        clause: "What counts as a cure",
        why: "If you do not say what would resolve it, a partial fix becomes an argument about whether the notice was satisfied.",
      },
      {
        clause: "Copy-to requirements",
        why: "Many agreements require a copy to counsel or a second address, and omitting it can invalidate the notice.",
      },
      {
        clause: "Whether you continue performing",
        why: "Shipping normally through a cure period sends a very different message from the one in the notice.",
      },
      {
        clause: "Whether the failure is curable at all",
        why: "Some failures cannot be cured, and issuing a cure notice may not be the right route to what you actually want.",
      },
    ],
    cpgIssues: [
      "A cure period on a co-packer failure has to be realistic against production scheduling, because a cure requiring a new run may be impossible inside a short window.",
      "For a distributor missing volume minimums, define the cure as measurable performance over a stated period rather than as a promise to try harder.",
      "Quality failures need an operational response — hold, test, withdraw — that runs whether or not a cure period is running, so the notice process must not slow the safety process.",
      "Where the failure involves product already in the market, say what should happen to that product during the cure period.",
      "Ingredient and packaging supplier failures often cannot be cured inside the lead time of the material, so the cure demanded should be specific about supply rather than about intent.",
      "For a 3PL or warehouse failure — inventory variance, mis-picks, temperature excursions — define the cure as a measured service level over a stated period, because one good week proves nothing.",
    ],
    founderMistakes: [
      "Sending a cure notice by email when the agreement required courier to a named address, and losing the termination right that depended on it.",
      "Counting the cure period from the day they sent it rather than from deemed receipt.",
      "Describing the problem without saying what a cure would look like, then arguing afterwards about whether it had been cured.",
      "Issuing a cure notice and continuing to place orders, undermining the position entirely.",
      "Missing a copy-to requirement buried in the notice clause.",
      "Setting a cure period shorter than the agreement allowed and terminating early.",
    ],
    negotiationPoints: [
      "Read the notice and cure clauses in full before drafting anything.",
      "State precisely what would constitute a cure and how it will be measured.",
      "Calculate the end date from deemed receipt under the agreement, and diarise it.",
      "Send by every method the agreement permits, including any required copies.",
      "Decide in advance what you will do if the cure is partial.",
      "Get counsel involved before sending if termination is the intended next step.",
    ],
    redFlags: [
      "Any notice sent without reading the notice clause.",
      "A cure period counted from the send date rather than from receipt.",
      "No description of what a cure would actually be.",
      "Copy-to requirements ignored.",
      "Continued ordering or performance that contradicts the notice.",
      "Terminating before the cure period has actually run.",
    ],
    beforeYouSign: [
      "The notice and cure provisions of the agreement, read in full and followed literally.",
      "A written statement of what a cure would look like and how you would measure it.",
      "A diarised end date calculated from deemed receipt.",
    ],
    related: [
      "breach-notice",
      "notice-of-termination",
      "demand-letter",
      "termination-agreement",
      "beverage-distributor-agreement",
    ],
    attorneyReview: "yes",
    templateId: "notice-to-cure",
    templateVersion: "1.0",
    lastReviewed: "2026-08-25",
  },

  {
    id: "notice-of-termination",
    name: "Notice of Termination",
    category: "disputes",
    subcategory: "Notices and demands",
    aliases: [
      "Termination Notice",
      "Notice of Non-Renewal",
      "Notice to Terminate",
    ],
    stages: ["distribution", "growth"],
    importance: "essential",
    entityTypes: ["any"],
    parties: "Brand + Counterparty",
    whenYouNeedIt:
      "When you are ending an agreement by exercising a right inside it: for cause after an uncured failure, for convenience on notice, or by declining to renew before an automatic renewal date.",
    whatItDoes:
      "Exercises the termination right, states which right you are relying on and on what basis, sets the effective date, and deals with what happens between the notice and that date.",
    whyItMatters:
      "Terminating a distributor incorrectly can turn a commercial disagreement into a much larger claim, and this is the document where that happens. The right you exercise, the notice period you give, the method you use and the reasons you state all come from the contract — and where the counterparty sits abroad, potentially from concepts in their own country that the contract never mentions. Getting the notice period wrong, or terminating for cause on facts that do not support it, converts a clean exit into a damages argument. Read the clause, take advice, then send.",
    majorTerms: [
      "The agreement and the termination right being exercised",
      "Whether termination is for cause or for convenience",
      "The facts, where cause is relied on",
      "The effective date and how it is calculated",
      "What happens between notice and effective date: orders, shipments, credit",
      "Treatment of inventory held by the counterparty",
      "Return of tooling, artwork, data and confidential information",
      "Accounts, customers and transition arrangements",
      "Which obligations survive",
      "Reservation of rights",
      "Method and address for notices, plus any required copies",
      "Signature by somebody authorised",
    ],
    clausesToWatch: [
      {
        clause: "Which termination right you are exercising",
        why: "Cause and convenience carry different notice periods and different consequences, and choosing wrongly can invalidate the notice.",
      },
      {
        clause: "Notice period and effective date",
        why: "Short notice can be ineffective, and an automatic renewal deadline missed by a week can bind you for another full term.",
      },
      {
        clause: "Whether stating reasons helps or hurts",
        why: "Reasons stated in a termination notice become the case you then have to stand behind, which is a question for counsel before sending.",
      },
      {
        clause: "Automatic renewal deadlines",
        why: "Many agreements renew unless notice is given well in advance, and that date is the real deadline rather than the term end.",
      },
      {
        clause: "Post-termination obligations",
        why: "Sell-off rights, inventory buy-backs and continuing supply obligations often survive the effective date and need planning for.",
      },
      {
        clause: "Local protections where the counterparty sits",
        why: "With distributors, agents and licensees abroad, ending the relationship can carry consequences the contract does not describe, which local counsel should assess first.",
      },
      {
        clause: "What happens in the interim",
        why: "Orders placed and shipped between notice and effective date need a clear position on credit, priority and pricing.",
      },
    ],
    cpgIssues: [
      "A terminated distributor holds inventory, holds accounts and may hold the retailer relationship, so the notice should be part of a transition plan rather than the whole of it.",
      "Ending a co-packer relationship means recovering tooling, specifications, retained samples and remaining materials, and the notice is when to state a deadline for each.",
      "Product in the market at termination keeps ageing, so agree what happens to it: buy-back, a sell-off with price limits, or return.",
      "Retail listings serviced by the outgoing party can go out of stock during a transition, and an out-of-stock at a chain can cost the listing entirely.",
      "Excess and obsolete raw materials and packaging held by a co-packer usually become payable on termination, so the exposure should be known before the notice goes out.",
      "Where the counterparty is in another country, local agent and distributor protections may shape what ending the relationship costs, which needs local advice before the notice rather than after it.",
      "Lot and quality records held by the terminated party need to stay available, because a withdrawal may still be needed on product they made or shipped.",
    ],
    founderMistakes: [
      "Missing an automatic renewal notice deadline by days and being locked into another full year with a distributor who was not performing.",
      "Terminating for cause on facts that did not support it, and converting a clean exit into a damages claim.",
      "Giving a shorter notice period than the agreement required and having the termination challenged.",
      "Terminating a foreign distributor with no local advice and facing a claim shaped by rules the contract never mentioned.",
      "Sending notice with no transition plan, and losing retail listings to out-of-stocks.",
      "Not asking for the tooling back in the notice, and negotiating for it from a much weaker position afterwards.",
    ],
    negotiationPoints: [
      "Diarise every automatic renewal and notice deadline in every material agreement, well ahead of time.",
      "Take counsel's view on which right to exercise, and on whether to state reasons at all.",
      "Take local advice before terminating an agent, distributor or licensee in another country.",
      "Set out in the notice what happens to inventory, tooling, records and accounts.",
      "Plan the customer transition before the notice goes out rather than after.",
      "Follow the notice clause exactly on method, address, copies and timing.",
    ],
    redFlags: [
      "Any termination notice sent without reading the notice and termination clauses.",
      "Terminating for cause with no evidence supporting the stated grounds.",
      "A notice period shorter than the agreement requires.",
      "Terminating a counterparty in another country with no local advice.",
      "No plan for inventory, tooling, records or accounts.",
      "An automatic renewal date discovered after it has already passed.",
    ],
    beforeYouSign: [
      "The agreement's termination, notice and renewal provisions, read in full.",
      "Counsel's view on the right being exercised, and local advice if the counterparty is abroad.",
      "A written transition plan covering inventory, accounts, tooling and records.",
      "A calculation of what termination costs: obsolete materials, buy-backs, transition support.",
    ],
    related: [
      "notice-to-cure",
      "breach-notice",
      "termination-agreement",
      "mutual-termination-agreement",
      "beverage-distributor-agreement",
    ],
    attorneyReview: "strongly-recommended",
    attorneyNote:
      "Ending a distributor, agent or licensee incorrectly can convert a commercial disagreement into a much larger claim, and where the counterparty sits abroad local protections may apply that the contract never mentions.",
    templateId: "notice-of-termination",
    templateVersion: "1.0",
    lastReviewed: "2026-08-25",
  },

  {
    id: "collection-demand",
    name: "Collection Demand",
    category: "disputes",
    subcategory: "Notices and demands",
    aliases: [
      "Past Due Notice",
      "Final Demand for Payment",
      "Dunning Letter",
    ],
    stages: ["launch", "distribution", "growth"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Brand + Non-Paying Customer",
    whenYouNeedIt:
      "When an invoice is materially overdue, ordinary reminders have not worked, and you want a formal, dated record of a demand for payment before escalating.",
    whatItDoes:
      "Sets out the invoices, amounts and due dates, states the total outstanding, demands payment by a stated date, and says what happens next: stop-ship, interest where the terms provide it, or referral to counsel or a collection professional.",
    whyItMatters:
      "Unpaid invoices are the most common dispute a CPG brand has, and they are usually lost in the paperwork rather than in the argument: no signed credit application, no agreed terms, product already delivered and consumed. A collection demand is worth sending, but the leverage it carries was mostly set months earlier when you decided to ship on open credit. The other reliable lever is the next order, which is why a stop-ship decision usually does more than the letter does.",
    majorTerms: [
      "Each invoice, its date, amount and due date",
      "Total outstanding",
      "Credits, returns or deductions already applied",
      "The agreed payment terms and where they came from",
      "Interest or late charges, if the terms provide for them",
      "A firm payment deadline",
      "Whether shipments are suspended",
      "What happens after the deadline",
      "Where and how to pay",
      "A contact for disputing specific invoices",
      "Reservation of rights",
      "Attachments: invoices, proof of delivery, signed terms",
    ],
    clausesToWatch: [
      {
        clause: "Whether your payment terms were ever agreed",
        why: "If the customer bought on their own purchase order terms, the terms you are enforcing may not be the terms actually in force.",
      },
      {
        clause: "Proof of delivery",
        why: "A demand without signed delivery documentation invites a dispute about receipt rather than about payment.",
      },
      {
        clause: "Deductions sitting inside the balance",
        why: "Much of what looks like non-payment in retail and distribution is deductions, which need identifying and answering separately.",
      },
      {
        clause: "Whether to keep shipping",
        why: "Continuing to supply a non-paying customer removes the leverage the letter was written to create.",
      },
      {
        clause: "Interest and late charges",
        why: "Charging them with no contractual basis weakens the credibility of everything else in the demand.",
      },
      {
        clause: "When to hand it over",
        why: "Once the balance is material or the customer has counsel, this stops being a letter you write yourself.",
      },
    ],
    cpgIssues: [
      "Deductions, spoils, promotional bill-backs and chargebacks make up much of the balance in distributor and retail accounts, so a demand that ignores them is answered with a spreadsheet rather than a payment.",
      "Product already delivered has usually been sold and consumed, so there is nothing to recover and no practical remedy other than payment.",
      "Distributors in financial difficulty typically hold your inventory as well as owing you money, and both need addressing in the same conversation.",
      "Deduction backup arrives slowly, so a demand should separate what is genuinely disputed from what is simply unpaid, or the whole balance gets treated as disputed.",
      "Independent and small-chain retail accounts frequently have no signed credit application at all, which is a lesson for the next account rather than for this one.",
      "Stopping shipment to a chain or a distributor has knock-on effects on listings and service levels, so the decision is commercial as much as financial.",
    ],
    founderMistakes: [
      "Shipping increasing volume to a distributor whose balance kept growing, because the orders looked like growth.",
      "Never obtaining a signed credit application or agreed terms, and having no documented basis for the demand.",
      "Sending reminders for months without ever stopping shipment.",
      "Charging interest that had never been agreed, and losing credibility on the whole demand.",
      "Treating a deduction-heavy balance as simple non-payment and receiving a line-by-line dispute in return.",
      "Continuing to negotiate directly long after the customer had put lawyers on it.",
    ],
    negotiationPoints: [
      "Attach the invoices, the delivery documentation and whatever agreed terms exist.",
      "Separate disputed deductions from undisputed unpaid invoices, and address each.",
      "Decide whether to stop shipping, and say so clearly in the letter.",
      "Set a firm date and then act on it.",
      "Offer a documented payment plan only with security or a written acknowledgement of the debt.",
      "Hand it to counsel or a collection professional once the amount is material or the other side has counsel.",
    ],
    redFlags: [
      "A growing balance on an account you keep shipping to.",
      "No signed terms, credit application or proof of delivery.",
      "Interest or charges with no contractual basis.",
      "A balance made up largely of deductions nobody has analysed.",
      "Repeated deadlines nobody enforces.",
      "Continuing to handle it yourself after the customer's lawyer has written.",
    ],
    beforeYouSign: [
      "A reconciled statement of invoices, credits, deductions and payments.",
      "Signed proof of delivery for the shipments concerned.",
      "Whatever agreed terms exist, and an honest view of whether they were ever accepted.",
    ],
    related: [
      "demand-letter",
      "settlement-agreement",
      "purchase-order-terms",
      "wholesale-supply-agreement",
      "food-distributor-agreement",
    ],
    attorneyReview: "depends",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "ip-cease-and-desist",
    name: "IP Cease-and-Desist",
    category: "disputes",
    subcategory: "Brand and confidentiality protection",
    aliases: [
      "Cease and Desist Letter",
      "Trademark Infringement Notice",
      "Takedown Demand",
    ],
    stages: ["growth"],
    importance: "situational",
    entityTypes: ["any"],
    parties: "Brand + Alleged Infringer",
    whenYouNeedIt:
      "When somebody is using your brand, packaging, photography or copy in a way you did not authorise: a copycat product, a marketplace listing, a lookalike label, an unauthorised reseller running your images.",
    whatItDoes:
      "Identifies the rights you hold, describes the specific use you object to, demands that it stop within a stated time, and sets out what you will do next if it does not.",
    whyItMatters:
      "A cease-and-desist is powerful and easy to misfire. Sent well, it stops a copycat cheaply. Sent badly, it produces a public screenshot, a fight you did not budget for, or a reply asking you to prove rights you never registered. The strength of the letter comes entirely from the position behind it — what you have registered, where, and how long you have used it — which is why it should go out with counsel rather than from a template found online. In a category where packaging trends move fast, telling infringement from category convention is exactly the judgement you are paying for.",
    majorTerms: [
      "The rights relied on: registrations, applications and use",
      "The specific infringing use, with dated evidence",
      "Why that use is objected to",
      "What must stop, and by when",
      "Removal, destruction or withdrawal of infringing material",
      "Any marketplace or platform takedown route being used in parallel",
      "Written confirmation required from the recipient",
      "Any interim conduct required",
      "Reservation of rights",
      "Tone, and who the letter will be read by",
      "Method of delivery and the record kept of it",
    ],
    clausesToWatch: [
      {
        clause: "The rights you actually hold",
        why: "A letter asserting rights you have not registered or used invites a reply that tests them, and that reply becomes part of the record.",
      },
      {
        clause: "How specific the objection is",
        why: "A broad complaint about a similar look is much weaker than a specific one about a mark, an image or a copied line of copy.",
      },
      {
        clause: "Tone",
        why: "An aggressive letter to a small maker is a screenshot waiting to be posted, and the reputational cost can exceed the infringement.",
      },
      {
        clause: "The deadline and what follows it",
        why: "Naming a consequence you will not pursue turns the letter into an invitation rather than a deterrent.",
      },
      {
        clause: "Whether a platform route is faster",
        why: "Marketplace and social takedown processes often resolve a listing in days where correspondence takes months.",
      },
      {
        clause: "Whether the recipient is a customer",
        why: "Unauthorised resellers are frequently people who bought your product, and the commercial answer may work better than the legal one.",
      },
    ],
    cpgIssues: [
      "Packaging trends move fast in CPG and much of what looks copied is category convention, so which specific elements you claim matters enormously.",
      "Copycat products from private-label programs raise a different commercial question, because the retailer running the program may also be your customer.",
      "Unauthorised marketplace resellers usually bought your product legitimately at retail or wholesale, so the leverage often sits in your sales terms rather than in a trademark claim.",
      "Product photography, label artwork and brand copy are often owned by the designer or photographer unless assigned, so check that you own what you are enforcing.",
      "Trademark rights are territorial, so a letter to an overseas copycat depends entirely on what you have filed in their country.",
      "A copycat sitting next to you on shelf may be the retailer's own item, which makes the commercial conversation more important than the letter.",
    ],
    founderMistakes: [
      "Sending a template cease-and-desist asserting rights they had never registered, and receiving a reply that made the position worse.",
      "Enforcing artwork they did not own, because the designer had never assigned it.",
      "Sending an aggressive letter to a small maker and having it posted publicly.",
      "Chasing an overseas copycat with no filing in that country.",
      "Using correspondence where the platform's own takedown process would have removed the listing in days.",
      "Threatening litigation with no budget and no intention behind it.",
    ],
    negotiationPoints: [
      "Confirm exactly what you own and where before writing anything.",
      "Be specific about the elements you object to, and attach dated evidence.",
      "Use platform takedown routes in parallel where they exist.",
      "Set a deadline you will act on, and say what happens next.",
      "Have counsel send it where the recipient is substantial or the position is arguable.",
      "Consider whether the commercial route — authorising, buying back, supplying directly — is better than the legal one.",
    ],
    redFlags: [
      "Asserting rights you have not registered or cannot evidence.",
      "Enforcing artwork or photography that was never assigned to you.",
      "A template letter sent without advice to a substantial counterparty.",
      "A threat with nothing behind it.",
      "Ignoring a faster platform takedown route.",
      "Sending it to a retailer who is also your customer without thinking about the relationship.",
    ],
    beforeYouSign: [
      "Your registration position for the marks, in the relevant territory.",
      "Written assignments for the artwork, photography and copy you are enforcing.",
      "Dated evidence of the infringing use, captured before it changes.",
      "Counsel's view on the strength of the position and on the tone of the letter.",
    ],
    related: [
      "trademark-assignment",
      "packaging-artwork-ownership",
      "copyright-assignment",
      "trademark-license-agreement",
      "demand-letter",
    ],
    attorneyReview: "strongly-recommended",
    attorneyNote:
      "The letter is only as strong as the registered rights and written assignments behind it, and a misfired notice can create a public dispute or a challenge to rights you have not secured.",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "confidentiality-breach-notice",
    name: "Confidentiality Breach Notice",
    category: "disputes",
    subcategory: "Brand and confidentiality protection",
    aliases: [
      "Notice of Breach of Confidentiality",
      "NDA Breach Notice",
      "Trade Secret Misuse Notice",
    ],
    stages: ["development", "manufacturing", "growth"],
    importance: "situational",
    entityTypes: ["any"],
    parties: "Brand + Party Under a Confidentiality Obligation",
    whenYouNeedIt:
      "When somebody bound by an NDA or a confidentiality clause appears to have used or disclosed your information: a formula shared with another client, a supplier list passed on, a departing contractor taking files, a co-packer making something suspiciously similar.",
    whatItDoes:
      "Identifies the confidentiality obligation, describes the apparent use or disclosure, demands that it stop, and requires return or certified destruction of the information along with an account of where it went.",
    whyItMatters:
      "Confidential information is only worth what you do about it when it starts moving, and the window is short — the value of a formula or a customer list falls the longer it circulates. Beyond stopping the behaviour, the notice does two useful things: it demands return or destruction of what was taken, and it asks who else has seen it, which is the question that tells you how bad the situation actually is. It also creates a dated record while files and memories still exist. Move quickly and involve counsel early, because preserving evidence is usually more urgent than the letter.",
    majorTerms: [
      "The agreement or clause creating the obligation",
      "The confidential information concerned, described carefully",
      "The apparent breach, dated and evidenced",
      "A demand that use and disclosure stop",
      "Return or certified destruction of all copies, including derivatives",
      "A written account of everyone the information reached",
      "Preservation of documents, devices and communications",
      "Any interim undertakings required",
      "Reservation of rights",
      "Method and address for notice under the agreement",
      "Whether a claim will follow",
      "Signature by somebody authorised",
    ],
    clausesToWatch: [
      {
        clause: "How the information is described",
        why: "A notice describing your trade secret in detail can circulate further than the original disclosure did, so the description needs real care.",
      },
      {
        clause: "Preservation of evidence",
        why: "Files, devices and messages disappear quickly once somebody knows they are being accused, so the preservation demand matters more than the accusation.",
      },
      {
        clause: "Return versus certified destruction",
        why: "A promise to delete, with no certification and no list of recipients, tells you nothing about where the information now sits.",
      },
      {
        clause: "Whether the obligation is still in force",
        why: "Many NDAs have a term, and an obligation that expired before the disclosure changes the position entirely.",
      },
      {
        clause: "Who else received it",
        why: "The practical harm depends on the spread, and the notice is the moment to ask for the full list.",
      },
      {
        clause: "Whether anyone else needs telling",
        why: "If the information belonged partly to a customer, supplier or partner, they may need to be notified, which is a question for counsel.",
      },
    ],
    cpgIssues: [
      "Formulas and specifications are the highest-value confidential information a CPG brand holds, and they usually sit with a co-packer, a flavour house and a lab simultaneously.",
      "A co-packer producing a similar product for another client is the classic scenario, and the notice should ask directly about use of your specification rather than only about disclosure.",
      "Supplier and sourcing information — who supplies you, at what price and to what specification — is often as valuable as the formula itself.",
      "Departing employees and contractors frequently leave with pricing files, customer lists and spec sheets, so device and account access should be addressed immediately.",
      "Packaging artwork and unlaunched product plans shared with printers, agencies and photographers travel widely, so establishing the recipient list comes first.",
      "Retail and distributor pricing disclosed under confidentiality can move between competitors, and that harm is immediate and purely commercial.",
    ],
    founderMistakes: [
      "Raising it informally on a call, giving the other side time to delete the evidence.",
      "Describing the confidential information in unnecessary detail in a notice that then circulated further.",
      "Never asking who else had received the information, so the real spread was never established.",
      "Accepting an assurance that everything had been deleted, with no certification and no recipient list.",
      "Discovering the confidentiality term had already expired before the disclosure happened.",
      "Handling it alone for months and losing the evidence that would have mattered.",
    ],
    negotiationPoints: [
      "Act quickly, and demand preservation of documents, devices and communications first.",
      "Require certified destruction or return, including derivatives and copies.",
      "Ask for a written account of everyone the information reached.",
      "Check the term and scope of the obligation before asserting it.",
      "Describe the information carefully rather than fully.",
      "Involve counsel early, because evidence preservation and next steps matter more than the letter itself.",
    ],
    redFlags: [
      "Delay, since every week reduces what can be recovered or proved.",
      "An assurance of deletion with no certification and no recipient list.",
      "A confidentiality obligation that has expired, or was never actually signed.",
      "A notice that restates the confidential information in full.",
      "No preservation demand made at all.",
      "Handling a serious formula or trade-secret issue without counsel.",
    ],
    beforeYouSign: [
      "The signed NDA or confidentiality clause, with its term and scope checked.",
      "Dated evidence of the apparent use or disclosure, preserved before it changes.",
      "A list of everyone who legitimately held the information, so you can work out where it moved.",
      "Counsel's involvement before the notice goes out, where the information is material.",
    ],
    related: [
      "mutual-nda",
      "manufacturer-nda",
      "trade-secret-protection-agreement",
      "formula-ownership-agreement",
      "demand-letter",
    ],
    attorneyReview: "yes",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },
];

export default DISPUTES_DOCS;
